/**
 * 백엔드 UnifiedApiResponse 형태에서 사용자에게 보여줄 문자열 추출.
 * - 성공/실패 모두 최상위 `message`를 우선 사용
 * - `error.violations[]` (Bean Validation 등)이 있으면 메시지를 합침
 * - 그 외 `error.message`, `error.reason` 등 보조 필드
 */
export function getUnifiedApiErrorMessage(payload: unknown): string {
  if (payload == null || typeof payload !== 'object') {
    return '요청 처리 중 오류가 발생했습니다.';
  }
  const p = payload as Record<string, unknown>;
  const top = p.message;
  if (typeof top === 'string' && top.trim()) {
    return top.trim();
  }
  const err = p.error;
  if (err && typeof err === 'object' && !Array.isArray(err)) {
    const e = err as Record<string, unknown>;
    if (Array.isArray(e.violations)) {
      const msgs = (e.violations as Array<Record<string, unknown>>)
        .map((v) => (typeof v.message === 'string' ? v.message : '').trim())
        .filter(Boolean);
      if (msgs.length) {
        return Array.from(new Set(msgs)).join(' ');
      }
    }
    if (typeof e.message === 'string' && e.message.trim()) {
      return e.message.trim();
    }
    if (typeof e.reason === 'string' && e.reason.trim()) {
      return e.reason.trim();
    }
  }
  if (typeof p.code === 'string' && p.code) {
    return `처리할 수 없습니다 (${p.code}).`;
  }
  return '요청 처리 중 오류가 발생했습니다.';
}

/** HTTP 에러 응답 본문이 UnifiedApiResponse인 경우 메시지 추출 */
export function getMessageFromAxiosErrorData(data: unknown): string {
  return getUnifiedApiErrorMessage(data);
}

/**
 * axios catch 블록용: 인터셉터가 붙인 normalizedMessage 우선, 이후 response.data 파싱.
 */
export function getApiFailureMessage(error: unknown, fallback = '요청 처리 중 오류가 발생했습니다.'): string {
  const anyErr = error as { normalizedMessage?: string; response?: { data?: unknown }; message?: string };
  if (typeof anyErr?.normalizedMessage === 'string' && anyErr.normalizedMessage.trim()) {
    return anyErr.normalizedMessage.trim();
  }
  const data = anyErr?.response?.data;
  if (data != null) {
    return getUnifiedApiErrorMessage(data);
  }
  if (typeof anyErr?.message === 'string' && anyErr.message.trim()) {
    return anyErr.message.trim();
  }
  return fallback;
}
